import { sortBy } from 'lodash-es';
import { makeAutoObservable } from 'mobx';

import {
  CatalogRequests,
  type CategoriesResponse,
  LandingPage,
} from '~/api/Catalog';
import { company } from '~/company/Company';
import { mainStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore/Category';
import type { ShopCategories } from '~/stores/CategoriesStore/ShopCategories';
import { shrinkCategoryTreeImages } from '~/stores/CategoriesStore/shrinkImages';
import { Pagination } from '~/stores/shared/Pagination';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';
import { PreloadCache } from '~/utils/preloadCache';
import toSpliced from '~/utils/toSpliced';

const PAGE_SIZE = 6;

export class HomeCategories {
  private readonly shopCategories: ShopCategories;

  constructor(
    shopCategories: ShopCategories,
    serverWarehouse?: string,
    serverLang?: string,
  ) {
    this.shopCategories = shopCategories;

    if (serverWarehouse) {
      this.init(serverWarehouse, serverLang);
    }

    makeAutoObservable(this);
  }

  public static preloadedData = new PreloadCache<CategoriesResponse>({
    max: 500,
    ttlMs: 60_000,
  });

  public static async preload(warehouse: string, serverLang?: string) {
    const data = await this.request(1, warehouse, serverLang);

    this.preloadedData.set(this.getCategoriesId(warehouse, serverLang), data);

    return data;
  }

  public static removePreloadedData(id: string) {
    this.preloadedData.delete(id);
  }

  public static getCategoriesId(warehouse: string, serverLang?: string) {
    return `${warehouse}.${serverLang}`;
  }

  private static request(page: number, warehouse: string, serverLang?: string) {
    return CatalogRequests.getCategories(
      {
        ...catalogWarehouseParam(warehouse),
        landingPage: [LandingPage.HOME],
        includes: { products: 15 },
        page: { size: PAGE_SIZE, current: page },
      },
      serverLang,
    ).then((response) => {
      // On production data this payload carried 117 402 B of product `images`
      // (url 38 192 + thumb 40 696 + orig 38 201) plus 118 668 B of
      // `variants[].images` — 30% of a 778 764 B serialized state.
      shrinkCategoryTreeImages(response.data);

      return response;
    });
  }

  public get list() {
    const data = this.paginationRequest.result;

    if (!data || !data.length) {
      return [];
    }

    const startShopping = this.shopCategories.startShoppingCategory;

    return startShopping && company.showStartShopping
      ? toSpliced(data, 1, 0, [startShopping])
      : data;
  }

  public readonly paginationRequest = new Pagination(
    async (page: number, warehouse: string) => {
      try {
        const response = await HomeCategories.request(page, warehouse);

        return this.convert(response);
      } catch (error) {
        return mainStore.errorHandler(error as never, 'requestCategories');
      }
    },
  );

  private init(serverWarehouse: string, serverLang?: string) {
    const isSSR = typeof window === 'undefined';
    const ssrDegraded = !isSSR && window.__INITIAL_STATE__?.ssrDegraded;
    const preloadedData = ssrDegraded
      ? null
      : (!isSSR ? window.__INITIAL_STATE__?.homeCategories : null) ||
        HomeCategories.preloadedData.get(
          HomeCategories.getCategoriesId(serverWarehouse, serverLang),
        );

    if (!preloadedData) {
      // Degraded SSR hydration or CSR without data. Do NOT fetch here: init()
      // runs during the render pass (store created in useState), and starting a
      // request mutates store state during render (StrictMode double-fetch, and
      // a render-time skeleton flip that can diverge from the server HTML). The
      // client fetch is triggered post-mount by the `useOnWarehouseChange`
      // effect in Root (guarded by `!homeCategories.list.length`).
      return;
    }

    this.paginationRequest.setState(this.convert(preloadedData));
  }

  private convert({
    data,
    pagination,
  }: Awaited<ReturnType<typeof CatalogRequests.getCategories>>) {
    return {
      pagination,
      data: sortBy(data, 'sorting')
        .filter(Category.validate)
        .map((data) => new Category(data)),
    };
  }
}
